import React, { useState, useMemo } from 'react';

// --- Reusable Formatting Functions (No changes needed here) ---
const formatPrice = (value) => {
  if (typeof value !== 'number' || value === 0) return 'N/A';
  return `$${value.toFixed(2)}`;
};

const formatNumber = (value) => {
  if (typeof value !== 'number') return 'N/A';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// eslint-disable-next-line no-unused-vars
const formatCurrency = (value) => {
  if (typeof value !== 'number') return 'N/A';
  const sign = value >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// eslint-disable-next-line no-unused-vars
const formatPercentage = (value) => {
    if (typeof value !== 'number') return 'N/A';
    const sign = value >= 0 ? '+' : '-';
    return `${sign}${Math.abs(value).toFixed(2)}%`;
};

// --- Main Table Component ---

function PortfolioPnlTable({ pnlData }) {

  // --- STEP 1: ADD SORTING STATE ---
  // We'll store the key of the column to sort by, and the direction ('asc' or 'desc')
  // Let's default to sorting by Market Value in descending order.
  const [sortConfig, setSortConfig] = useState({ key: 'market_value', direction: 'desc' });

  // --- STEP 2: MEMOIZE THE SORTED DATA ---
  // This is very efficient. The sorting logic only re-runs if the pnlData or sortConfig changes.
  const sortedData = useMemo(() => {
    if (!pnlData || pnlData.length === 0) return [];
    
    // Create a mutable copy of the data to sort
    let sortableItems = [...pnlData];

    // Apply the sorting logic
    sortableItems.sort((a, b) => {
      // Handle cases where data might be null or not a number
      const valA = a[sortConfig.key] ?? -Infinity;
      const valB = b[sortConfig.key] ?? -Infinity;

      if (valA < valB) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sortableItems;
  }, [pnlData, sortConfig]);

  // --- STEP 3: CREATE A FUNCTION TO HANDLE HEADER CLICKS ---
  const requestSort = (key) => {
    let direction = 'asc';
    // If you click the same column again, reverse the sort direction
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // --- STEP 4: CREATE A HELPER COMPONENT FOR THE SORT ICON ---
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };


  if (!pnlData || pnlData.length === 0) {
    return <p>No holdings data available to display.</p>;
  }

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            {/* Make each header a button that calls requestSort */}
            <th onClick={() => requestSort('ticker')}>Ticker<SortIndicator columnKey="ticker" /></th>
            <th onClick={() => requestSort('shares_held')}>Shares<SortIndicator columnKey="shares_held" /></th>
            <th onClick={() => requestSort('avg_cost')}>Avg. Cost<SortIndicator columnKey="avg_cost" /></th>
            <th onClick={() => requestSort('current_price')}>Price<SortIndicator columnKey="current_price" /></th>
            <th onClick={() => requestSort('market_value')}>Market Value<SortIndicator columnKey="market_value" /></th>
            <th onClick={() => requestSort('daily_pnl')}>Daily P&L<SortIndicator columnKey="daily_pnl" /></th>
            <th onClick={() => requestSort('total_pnl')}>Total P&L<SortIndicator columnKey="total_pnl" /></th>
            <th onClick={() => requestSort('pnl_percentage')}>P&L %<SortIndicator columnKey="pnl_percentage" /></th>
          </tr>
        </thead>
        <tbody>
          {/* Render the SORTED data, not the original pnlData */}
          {sortedData.map((item) => (
            <tr key={item.ticker}>
              <td><strong>{item.ticker}</strong></td>
              <td>{formatNumber(item.shares_held)}</td>
              <td>{formatPrice(item.avg_cost)}</td>
              <td>{formatPrice(item.current_price)}</td>
              <td>${formatNumber(item.market_value)}</td>
              <td className={item.daily_pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                ${(item.daily_pnl ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className={item.total_pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                ${(item.total_pnl ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className={item.pnl_percentage >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                {(item.pnl_percentage ?? 0).toFixed(2)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PortfolioPnlTable;